/**
 * 文件上传组件（v4.0 重构）
 * 使用 FileDropzone 大面积拖拽区替代小按钮
 */
import React, { useState } from 'react';
import { Button, Card, Alert, Space, Typography, Upload } from 'antd';
import { KeyOutlined, UploadOutlined, CrownOutlined } from '@ant-design/icons';
import { useUser } from '../contexts/UserContext';
import FileDropzone from './FileDropzone';

const { Text, Paragraph } = Typography;

const FileUpload = ({ onUploadSuccess }) => {
  const { isTester } = useUser();
  const [excelFile, setExcelFile] = useState(null);
  const [keyFile, setKeyFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleExcelSelect = (file) => {
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    if (!isExcel) {
      setError('请上传 .xlsx 或 .xls 格式的文件');
      return;
    }
    const isLt50M = file.size / 1024 / 1024 < 50;
    if (!isLt50M) {
      setError('文件大小不能超过 50MB');
      return;
    }
    setExcelFile(file);
    setError(null);
  };

  const handleKeySelect = (file) => {
    const isJson = file.name.endsWith('.json');
    if (!isJson) {
      setError('请上传 .json 格式的 Service Account 密钥文件');
      return false;
    }
    const isLt10K = file.size / 1024 < 10;
    if (!isLt10K) {
      setError('密钥文件过大，请确认是否为正确的 Service Account 密钥');
      return false;
    }
    setKeyFile(file);
    setError(null);
    return false;
  };

  const handleUpload = async () => {
    if (!excelFile) {
      setError('请选择 Excel 文件');
      return;
    }
    if (!isTester() && !keyFile) {
      setError('请选择 Excel 文件和密钥文件');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await onUploadSuccess(excelFile, isTester() ? null : keyFile);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  const canUpload = excelFile && (isTester() || keyFile) && !uploading;

  return (
    <Card
      title="上传文件"
      style={{ marginBottom: 24 }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* 内测用户提示 */}
        {isTester() && (
          <Alert
            message={
              <Space>
                <CrownOutlined style={{ color: '#faad14' }} />
                <span>内测用户：您无需上传 API Key，系统将自动使用默认配置</span>
              </Space>
            }
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
        )}

        {/* Service Account 密钥上传（仅非内测用户显示） */}
        {!isTester() && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              <KeyOutlined /> Service Account 密钥
            </Text>
            <Upload
              accept=".json"
              beforeUpload={handleKeySelect}
              onRemove={() => setKeyFile(null)}
              fileList={keyFile ? [keyFile] : []}
            >
              <Button icon={<UploadOutlined />} disabled={uploading}>
                选择密钥文件 (.json)
              </Button>
            </Upload>
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
              你的密钥仅用于本次打标，处理完成后会自动删除
            </Paragraph>
          </div>
        )}

        {/* Excel 文件拖拽上传区 */}
        <FileDropzone
          accept=".xlsx,.xls"
          onFileSelect={handleExcelSelect}
          selectedFile={excelFile}
        />

        {/* 错误提示 */}
        {error && (
          <Alert
            message="上传失败"
            description={error}
            type="error"
            closable
            onClose={() => setError(null)}
          />
        )}

        {/* 上传按钮 */}
        <Button
          type="primary"
          size="large"
          onClick={handleUpload}
          disabled={!canUpload}
          loading={uploading}
          style={{ width: '100%' }}
        >
          {uploading ? '上传中...' : '确认上传'}
        </Button>
      </Space>
    </Card>
  );
};

export default FileUpload;
